/**
 * @author      david.loehr@zumper.com (David Loehr)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { Flex, Image } from '@chakra-ui/react'
import { calendarGray600, calendarGray900 } from '@zcode-ui/icons'
import { selectIsMounted } from '@zcode-ui/web-zumper-global-state'
import type { DetailPageContextType } from '@zcode-ui/web-zumper-pages'
import { PropertyFeatures } from '@zcode/enums'
import { AnalyticsC } from '@zcode/nr-utils'
import React from 'react'
import { type GetProps, useSelector } from 'react-redux'
import { getRequestedTour } from '../../actions/sendMessage/requestedTours'
import { TourButtonContainer } from '../../components/EqualCTAs/TourButton/TourButtonContainer'

type DetailTourButtonProps = GetProps<DetailPageContextType['TourButton']>

const tourButtonContent = (isMessaged: boolean, hasInstantTourAvailability: boolean) => (
  <Flex alignItems="center">
    {isMessaged ? (
      <React.Fragment>
        <Image alt="" mr={1} src={calendarGray600} />
        Tour requested
      </React.Fragment>
    ) : (
      <React.Fragment>
        <Image alt="" mr={1} src={calendarGray900} />
        {hasInstantTourAvailability ? 'Book tour' : 'Request tour'}
      </React.Fragment>
    )}
  </Flex>
)

const tourButtonContentOutline = (isMessaged: boolean, hasInstantTourAvailability: boolean) =>
  isMessaged ? 'Tour requested' : hasInstantTourAvailability ? 'Book tour' : 'Request tour'

export const DetailTourButton = ({ buttonProps, ...props }: DetailTourButtonProps) => {
  const { entity, selectedFloorplan } = props
  const appIsMounted = useSelector(selectIsMounted)
  if (!entity) {
    return null
  }

  if (
    entity.isExternal ||
    entity.features?.includes(PropertyFeatures.TOUR_DISABLED) ||
    !entity.isActive
  ) {
    return null
  }
  const requestedTour = appIsMounted && getRequestedTour(selectedFloorplan ?? entity)
  return (
    <TourButtonContainer
      analyticChannel={AnalyticsC.FULL_DETAIL}
      buttonProps={{ className: 'flex', variant: 'ghost', size: 'lg', ...buttonProps }}
      disableOneTap
      isMessaged={!!requestedTour}
      {...props}
    >
      {buttonProps?.variant === 'outline' ? tourButtonContentOutline : tourButtonContent}
    </TourButtonContainer>
  )
}
